import { z } from 'zod';
import { lookSchema } from './look.js';
import type { Look } from './look.js';
import type { Currency } from './premium.js';

/**
 * Player-to-player trading.
 *
 * One player presses Trade on another; the other accepts; both then put items
 * and money (Kaki Dollars, Gold or Diamonds) on the table and each confirms.
 * Changing an offer clears both confirmations, so nobody can swap something
 * after the other has agreed. When both have confirmed, the server checks that
 * everything is still owned and moves it all in one atomic step.
 *
 * Everything is HTTP under /api/trade; the server pushes `trade:` messages so
 * the other player's window updates at once.
 */

export const MAX_TRADE_ITEMS = 20;
/** A request nobody answers is dropped after this long. */
export const TRADE_REQUEST_TTL_MS = 2 * 60_000;
/** An accepted trade nobody touches is dropped after this long. */
export const TRADE_IDLE_TTL_MS = 15 * 60_000;

export const TRADE_STATUSES = ['pending', 'active', 'completed', 'declined', 'cancelled', 'expired'] as const;
export type TradeStatus = (typeof TRADE_STATUSES)[number];

/** What one side puts on the table. Item ids are inventory item ids. */
export const tradeOfferSchema = z.object({
  money: z.number().int().min(0).max(1_000_000_000),
  gold: z.number().int().min(0).max(10_000_000),
  diamond: z.number().int().min(0).max(10_000_000),
  itemIds: z.array(z.string().min(1).max(64)).max(MAX_TRADE_ITEMS),
});
export type TradeOfferInput = z.infer<typeof tradeOfferSchema>;

export const tradeOfferBodySchema = z.object({ offer: tradeOfferSchema });
export const tradeConfirmBodySchema = z.object({ confirmed: z.boolean() });

export interface TradeOfferView {
  money: number;
  gold: number;
  diamond: number;
  items: Array<{ id: string; kind: string }>;
  confirmed: boolean;
}

export interface TradePartner {
  id: string;
  displayName: string;
  look: Look;
}

/** A trade as one of its two players sees it. */
export interface TradeView {
  id: string;
  status: TradeStatus;
  /** True when this player started the trade. */
  iStarted: boolean;
  partner: TradePartner;
  mine: TradeOfferView;
  theirs: TradeOfferView;
  createdAt: number;
  updatedAt: number;
}

export function offerIsEmpty(offer: Pick<TradeOfferInput, 'money' | 'gold' | 'diamond' | 'itemIds'>): boolean {
  return offer.money === 0 && offer.gold === 0 && offer.diamond === 0 && offer.itemIds.length === 0;
}

export function offerAmount(offer: TradeOfferInput | TradeOfferView, currency: Currency): number {
  return offer[currency];
}

export const tradeServerMessageSchema = z.discriminatedUnion('t', [
  /** Someone wants to trade with you. */
  z.object({
    t: z.literal('trade:request'),
    tradeId: z.string(),
    from: z.object({ id: z.string(), displayName: z.string(), look: lookSchema }),
  }),
  /** The trade changed (accepted, offer edited, confirmed, finished, cancelled): refetch it. */
  z.object({ t: z.literal('trade:changed'), tradeId: z.string(), status: z.enum(TRADE_STATUSES) }),
]);
export type TradeServerMessage = z.infer<typeof tradeServerMessageSchema>;
